/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-09 11:39:10
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Page,
  Layout,
  Button,
  TextStyle,
  Card,
  FormLayout,
  SkeletonDisplayText,
  ProgressBar,
  Stack,
  Tooltip,
  Icon
} from '@shopify/polaris';
import { QuestionMarkMajor } from '@shopify/polaris-icons';
import { Field, reduxForm, getFormValues } from 'redux-form';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import type { FormProps } from 'redux-form';

import { RenderSelect2 } from 'src/components/views/common/forms/render-select-2';
import { StepProgress } from 'src/components/views/theme-upgrade/step-progress';
import ThemeLimitErrorModal from 'src/components/views/theme-upgrade/theme-limit-error-modal';
import ThemeErrorModal from 'src/components/containers/common/theme-error-modal';
import * as themeSetupAction from 'actions/theme-setup-actions';
import * as setupAction from 'actions/setup-actions';
import { routes } from 'src/routes';
import history from 'src/routes/history';

type Props = {
  theme: Object,
  themes: Array<Object>,
  getThemesLoading: boolean,
  getUpgradeThemeStatusLoading: boolean,
  autoSetupThemeLoading: boolean,
  copyProcess: number,
  setupThemeProcess: number,
  copyAssetsProgress: string,
  themeUpgradeStatus: string,
  updatedAt: string,
  themeName: string,
  themeId: string,
  getThemes: () => void,
  autoSetupTheme: (data: { theme: string, layout: string }) => void,
  getUpgradeThemeStatus: () => void,
  resetUpgradeThemeStatus: () => void
};

type ComposedProps = Props & WithI18nProps & FormProps;

type State = {
  runningUpgrade: boolean,
  activeModalError: boolean,
  activeLimitModalError: boolean
};

class ThemeUpgrade extends Component<ComposedProps, State> {
  state = {
    runningUpgrade: false,
    activeModalError: false,
    activeLimitModalError: false
  };

  componentDidMount() {
    const { getThemes } = this.props;
    getThemes();
  }

  componentWillReceiveProps(nextProps) {
    const { getUpgradeThemeStatusLoading, copyAssetsProgress, themeUpgradeStatus, updatedAt } =
      nextProps;
    const { runningUpgrade } = this.state;

    if (this.props.getUpgradeThemeStatusLoading && !getUpgradeThemeStatusLoading) {
      // Check if the install theme is hanged
      const isHangedInstallTheme = this.checkHangInstallTheme(themeUpgradeStatus, updatedAt);

      if (themeUpgradeStatus === 'error' && copyAssetsProgress === 'reachedLimit') {
        // show limit modal error
        this.setState({ activeLimitModalError: true });
      } else if (themeUpgradeStatus === 'error' || isHangedInstallTheme) {
        // show common modal error
        this.setState({ activeModalError: true });
      } else {
        const { copyProcess, setupThemeProcess, themeId, themeName } = nextProps;
        const progress = copyProcess + setupThemeProcess;

        // Continue to get status if there is no error or hanging
        if (progress < 100 && runningUpgrade) {
          const { getUpgradeThemeStatus } = this.props;
          setTimeout(() => getUpgradeThemeStatus(), 1000);
        }

        // Go to next page when progress is done
        if (progress >= 100 && runningUpgrade) {
          setTimeout(() => {
            const historyState = {
              themeId,
              themeName
            };

            const { resetUpgradeThemeStatus } = this.props;

            // reset state before go to next page
            this.setState({ runningUpgrade: false });
            resetUpgradeThemeStatus();

            // Go to success page
            history.push({
              pathname: routes.themeUpgrade.success(),
              state: historyState
            });
          }, 10000);
        }
      }
    }
  }

  handleRefresh = () => {
    const { getThemes } = this.props;
    getThemes();
  };

  render() {
    const {
      i18n,
      theme,
      themes,
      handleSubmit,
      pristine,
      submitting,
      autoSetupThemeLoading,
      getThemesLoading,
      copyProcess,
      setupThemeProcess
    } = this.props;

    const { runningUpgrade, activeModalError, activeLimitModalError } = this.state;

    const themesList = themes
      ? themes.map(item => ({
          value: item.id,
          label: `${item.name}${
            item.published
              ? ` ${i18n.translate('AdminHTML.ThemeUpgrade.ThemeUpgrade.suffixThemeLive')}`
              : ''
          }`,
          published: item.published,
          layout: item.layout
        }))
      : [];

    let progress = copyProcess + setupThemeProcess;
    progress = progress % 1 === 0 ? progress.toFixed(0) : progress.toFixed(2);

    return (
      <div className="BC-Page BC-Page__Theme-Upgrade">
        <Page
          title={i18n.translate('AdminHTML.ThemeUpgrade.ThemeUpgrade.title')}
          primaryAction={<StepProgress step={1} />}>
          <Layout>
            <Layout.Section>
              <Card>
                <Card.Section>
                  <div className="BC-Page BC-Page__Text">
                    <TextStyle>
                      {i18n.translate('AdminHTML.ThemeUpgrade.ThemeUpgrade.pageText', {
                        t1: (
                          <TextStyle variation="strong">
                            {i18n.translate('AdminHTML.ThemeUpgrade.ThemeUpgrade.pageText_t1')}
                          </TextStyle>
                        ),
                        nt_new_line_character: <br />
                      })}
                    </TextStyle>
                  </div>
                </Card.Section>

                <Card.Section>
                  <form className="BC-Theme-Upgrade_Form">
                    <FormLayout>
                      <div className="BC-Theme-Upgrade_Form-Item">
                        <div className="title">
                          <TextStyle variation="strong">
                            {i18n.translate('AdminHTML.ThemeUpgrade.ThemeUpgrade.selectTheme')}
                          </TextStyle>
                        </div>

                        <Stack spacing="tight" alignment="center">
                          <div className="RenderSelect2__Wrapper">
                            {getThemesLoading ? (
                              <SkeletonDisplayText size="medium" />
                            ) : (
                              <Field
                                name="theme"
                                component={RenderSelect2}
                                options={themesList}
                                placeholder={i18n.translate(
                                  'AdminHTML.ThemeUpgrade.ThemeUpgrade.theme_Placeholder'
                                )}
                                placeholderEmpty={i18n.translate(
                                  'AdminHTML.ThemeUpgrade.ThemeUpgrade.theme_PlaceholderEmpty'
                                )}
                                isDisabled={_.isEmpty(themesList) || runningUpgrade}
                              />
                            )}
                          </div>
                          <Stack spacing="extraTight" alignment="center">
                            <Button
                              plain
                              removeUnderline
                              onClick={this.handleRefresh}
                              disabled={getThemesLoading || submitting || runningUpgrade}>
                              <TextStyle variation="strong">
                                {i18n.translate(
                                  'AdminHTML.ThemeUpgrade.ThemeUpgrade.refreshButton'
                                )}
                              </TextStyle>
                            </Button>
                            <span className="tooltip">
                              <Tooltip
                                content={i18n.translate(
                                  'AdminHTML.ThemeUpgrade.ThemeUpgrade.tooltipContent',
                                  { nt_new_line_character: <br /> }
                                )}
                                preferredPosition="above"
                                light>
                                <Icon source={QuestionMarkMajor} color="base" />
                              </Tooltip>
                            </span>
                          </Stack>
                        </Stack>
                      </div>
                      <div className="upgrade-action">
                        {runningUpgrade ? (
                          <div className="upgrade-progress">
                            <ProgressBar progress={progress} />
                            <span className="upgrade-inline-text">
                              {i18n.translate('AdminHTML.ThemeUpgrade.ThemeUpgrade.upgradeRunning')}
                            </span>
                          </div>
                        ) : (
                          <Button
                            primary
                            onClick={handleSubmit(this.runUpgrade.bind(this))}
                            loading={autoSetupThemeLoading}
                            disabled={
                              pristine || submitting || getThemesLoading || _.isEmpty(theme)
                            }>
                            {i18n.translate('AdminHTML.ThemeUpgrade.ThemeUpgrade.runUpgrade')}
                          </Button>
                        )}
                      </div>
                    </FormLayout>
                  </form>
                </Card.Section>
              </Card>
            </Layout.Section>
          </Layout>
          <ThemeErrorModal
            active={activeModalError}
            handleChange={() => this.toggleChangeState('activeModalError')}
          />
          <ThemeLimitErrorModal
            active={activeLimitModalError}
            handleChange={() => this.toggleChangeState('activeLimitModalError')}
          />
        </Page>
      </div>
    );
  }

  // Toggle change state, use for modal only
  toggleChangeState = (id: string) => {
    const value = this.state[id];
    this.setState({ [id]: !value });

    // Reset state
    this.setState({ runningUpgrade: false });
    this.props.resetUpgradeThemeStatus();
  };

  checkHangInstallTheme = (status, updatedAt) => {
    if (status === 'running' && updatedAt) {
      const dateDiff = Math.floor(Date.now() / 1000) - updatedAt;
      // The install theme is hanged if last updated is bigger than 15 mins
      if (dateDiff > 900) {
        return true;
      }
    }

    return false;
  };

  // Run upgrade
  runUpgrade = async data => {
    const { autoSetupTheme, getUpgradeThemeStatus } = this.props;
    const { activeModalConfirmSetup } = this.state;

    // Handle if the confirm modal is active
    if (activeModalConfirmSetup) {
      this.setState({ activeModalConfirmSetup: false });
    }

    // Run upgrade
    let prettyData = {
      requestParams: {
        themeId: data.theme.value,
        layout: data.theme.layout ? data.theme.layout.replace('-', '') : data.theme.layout
      },
      requestAction: 'upgrade-to-v2'
    };
    await autoSetupTheme(prettyData);

    // Call to get status
    this.setState({ runningUpgrade: true });
    getUpgradeThemeStatus();
  };
}

const ThemeUpgradeForm = reduxForm({
  form: 'ThemeUpgrade', // a unique identifier for this form
  enableReinitialize: true
})(ThemeUpgrade);

const mapStateToProps = (state, props) => {
  const { themeSetup, setup } = state;
  const formValues = getFormValues('ThemeUpgrade')(state);

  return {
    theme: formValues && formValues.theme ? formValues.theme : {},
    themes: _.get(themeSetup, 'themes', []),
    getThemesLoading: _.get(themeSetup, 'getThemesLoading', true),
    getUpgradeThemeStatusLoading: _.get(setup, 'getUpgradeThemeStatusLoading', false),
    autoSetupThemeLoading: _.get(setup, 'autoSetupThemeLoading', false),
    copyProcess: _.get(setup, 'copyProcess', 0),
    setupThemeProcess: _.get(setup, 'setupThemeProcess', 0),
    copyAssetsProgress: _.get(setup, 'copyAssetsProgress', ''),
    themeUpgradeStatus: _.get(setup, 'themeUpgradeStatus', ''),
    updatedAt: _.get(setup, 'updatedAt', ''),
    themeName: _.get(setup, 'themeName', ''),
    themeId: _.get(setup, 'themeId', '')
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getThemes: () => dispatch(themeSetupAction.getThemes(ownProps.i18n)),
  autoSetupTheme: (data: { theme: string, layout: string }) =>
    dispatch(setupAction.autoSetupTheme(data, true, ownProps.i18n)),
  getUpgradeThemeStatus: () => dispatch(setupAction.getUpgradeThemeStatus(ownProps.i18n)),
  resetUpgradeThemeStatus: () => dispatch(setupAction.resetUpgradeThemeStatus())
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(ThemeUpgradeForm));
